import { useFormik } from 'formik';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import Select from 'react-select';
import { Card } from '@components/card/card';
import { CardBody, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useAppDispatch } from '@store/hooks';
import { addMoveTransactions } from '@store/INV/Transactions/InvMove/invMoveSlice';
import { useNavigate } from 'react-router-dom';
import useGetTransactionTypesMoveList from '@hooks/INV/InvMove/useGetTransactionTypesMoveList';
import useGetInventoryWithPermission from '@hooks/NTX/Organization/useGetInventoryWithPermission';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import MoveLines from './MoveLines';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import NeatixModal from '@components/Modal/NeatixModal';
import { useState } from 'react';

// ✅ NEW: نفس lookup المستخدم في MoveLines للتحقق قبل الحفظ
import { fetchItemsWithUomLookup } from '@pages/INV/lookups/itemsWithUomLookup.api';
import { AddMoveTransactionsSchema } from '@pages/ValidationForm/validationSchema';
import useGetOrganizations from '@hooks/HR/use-get-organizations';

const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// ✅ يتحقق أن كل itemId موجود في inventoryTo (اختياري: subInventoryTo)
const validateItemsExistInInventoryTo = async (vals: any) => {
  const destInventoryId = vals?.inventoryTo;
  if (!destInventoryId) return { ok: true, missing: [] as string[] };

  const lines = Array.isArray(vals?.materialTransactionsLineList) ? vals.materialTransactionsLineList : [];
  const itemIds = Array.from(
    new Set(lines.filter((l: any) => l?.transactionItemId).map((l: any) => String(l.transactionItemId)))
  );

  if (itemIds.length === 0) return { ok: true, missing: [] as string[] };

  const params: any = { InventoryId: destInventoryId, Scope: 1 };
  if (vals?.subInventoryTo) params.SubInventoryId = vals.subInventoryTo;

  const found = new Set<string>();
  const controller = new AbortController();

  for (const idsChunk of chunk(itemIds, 200)) {
    const resp = await fetchItemsWithUomLookup(
      params,
      {
        pageIndex: 1,
        pageSize: 3000,
        itemType: 'InventoryItem',
        itemIds: idsChunk
      } as any,
      controller.signal
    );

    const list = resp?.data?.data || [];
    for (const r of list) {
      const id = String((r as any)?.itemId ?? '');
      if (id) found.add(id);
    }
  }

  const missing = itemIds.filter((id: any) => !found.has(id));
  return { ok: missing.length === 0, missing };
};

const Add = () => {
  const [showModal, setShowModal] = useState(true);

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { OperatingUnitOptions } = useGetOperatingUnitWithPermission();
  const { InventoryOptions, loading: loading3 } = useGetInventoryWithPermission();
  const { TreeSubInventoryData } = useGetSubinventoryList();
  const { OptionsTransactionTypes } = useGetTransactionTypesMoveList();

  const { OperatingUnitOptions: OperatingUnitOptions2, InventoryOptions: InventoryOptions2 } = useGetOrganizations(); // وحدات التشغيل والمخازن بدون صلاحيـات

  const formik = useFormik({
    initialValues: {
      code: 'MOV',
      branchId: '',
      branchTo: '',
      inventoryId: '',
      inventoryTo: '',
      subInventoryId: null,
      subInventoryTo: null,
      transactionDate: new Date().toISOString().split('T')[0],
      description: '',
      transactionAmount: 0,
      referenceType: '',
      referenceId: '',
      supplierId: null,
      customerId: null,
      personId: null,
      address: '',
      note: '',
      referenceCode: '',
      materialTransactionsLineList: [] as any[],
      // ✅ 1 = بدون سعر (لا يظهر عمود السعر)
      // ✅ 2 = بسعر يدوي (يظهر عمود السعر)
      methodTransfer: 1
    },
    enableReinitialize: true,
    // validationSchema: AddMoveTransactionsSchema(t),
    validationSchema: AddMoveTransactionsSchema(t),
    validateOnChange: true,
    validateOnBlur: true,

    validate: async values => {
      try {
        await AddMoveTransactionsSchema(t).validate(values, {
          abortEarly: false,
          context: { methodTransfer: Number(values.methodTransfer) }
        });
        return {};
      } catch (err: any) {
        const errors: any = {};
        if (err?.inner?.length) {
          err.inner.forEach((e: any) => {
            if (!errors[e.path]) errors[e.path] = e.message;
          });
        }
        return errors;
      }
    },

    // ✅ async حتى نعمل التشييك قبل الحفظ
    onSubmit: async values => {
      try {
        // ✅ 1) تحقق قبل الحفظ: كل الأصناف موجودة في inventoryTo
        const check = await validateItemsExistInInventoryTo(values);
        if (!check.ok) {
          formik.setSubmitting(false);

          const preview = check.missing.slice(0, 10).join(', ');
          CustomAlert({
            action: 'Error',
            msg:
              t('لا يمكن حفظ التحويل لأن بعض الأصناف غير موجودة في المخزن المحول إليه') + ` (${check.missing.length})`
          });
          return;
        }

        // ✅ 2) إذا OK كمل الحفظ
        dispatch(
          addMoveTransactions({
            code: values.code,
            branchId: values.branchId,
            branchTo: values.branchTo || null,
            inventoryId: values.inventoryId,
            inventoryTo: values.inventoryTo || null,
            subInventoryId: values.subInventoryId || null,
            subInventoryTo: values.subInventoryTo || null,
            transactionDate: values.transactionDate,
            description: values.description,
            transactionAmount: 0,
            referenceType: values.referenceType,
            referenceId: values.referenceId || null,
            supplierId: values.supplierId || null,
            customerId: values.customerId || null,
            personId: values.personId || null,
            address: values.address,
            note: values.note,
            referenceCode: '',
            methodTransfer: values.methodTransfer,
            materialTransactionsLineList: (values.materialTransactionsLineList || []).map((item: any) => ({
              transactionItemId: item.transactionItemId,
              transactionUom: item.transactionUom,
              // ✅ إذا methodTransfer=1 ممنوع سعر => نرسل 0
              // ✅ إذا methodTransfer=2 نرسل سعر المستخدم
              unitPrice: Number(values.methodTransfer) === 2 ? Number(item.unitPrice || 0) : 0,
              transactionQuantityIn: item.transactionQuantityIn,
              transactionQuantityOut: 0,
              inventoryId: null,
              discount: 0,
              currencyCode: '',
              currencyConversionRate: 0,
              referenceIdMaster: null,
              referenceIdLine: null,
              transactionTypeId: null,
              batchId: null,
              inventoryTo: null
            }))
          })
        )
          .unwrap()
          .then((res: any) => {
            if (res?.succeeded === true) {
              CustomAlert({ action: 'Add' });
              navigate(-1);
            } else {
              formik.setSubmitting(false);
              CustomAlert({ action: 'Error', msg: res?.message });
            }
          })
          .catch(() => {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error' });
          });
      } catch (e) {
        formik.setSubmitting(false);
        CustomAlert({ action: 'Error', msg: t('تعذر التحقق من الأصناف في المخزن المحول إليه') });
      }
    }
  });

  const { values, handleChange: formikhandleChange, errors, touched } = formik;

  console.log(errors);

  // ✅ لو المستخدم غيّر سياق التحويل صفّر البنود
  // useEffect(() => {
  //   formik.setFieldValue('materialTransactionsLineList', []);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [values.inventoryId, values.subInventoryId, values.inventoryTo, values.subInventoryTo]);

  const handleModalSubmit = () => {
    if (!values.branchId || !values.inventoryId || !values.code || !values.methodTransfer) {
      return;
    }
    setShowModal(false);
  };

  return (
    <>
      <Breadcrumb
        BreadcrumbList={[
          { name: 'Home', link: ':moduleHome' },
          { name: 'أوامر التحويــل', link: '' },
          { name: 'إضافة أمر تحويل', link: null }
        ]}
        PageName='إضافة أمر تحويــل'
      />

      <Form onSubmit={formik.handleSubmit}>
        <Card className='shadow'>
          <CardBody>
            <div className='row'>
              <div className='col-lg-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('نوع الحركة')}</label>
                  <Select
                    isDisabled={!showModal}
                    classNamePrefix='react-select'
                    isSearchable
                    isClearable
                    options={OptionsTransactionTypes}
                    value={OptionsTransactionTypes?.find(f => f.value === values.code)}
                    onChange={(option: any) => formik.setFieldValue('code', option === null ? null : option.value)}
                    placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                  />
                  <div className='text-danger'>{formik.errors.code as any}</div>
                </div>
              </div>

              <div className='col-lg-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('تاريخ التحويل')}</label>
                  <Form.Control
                    className='form-control text-center'
                    type='date'
                    name='transactionDate'
                    onChange={formikhandleChange}
                    value={values.transactionDate || ''}
                  />
                  <div className='text-danger'>{formik.errors.transactionDate as any}</div>
                </div>
              </div>

              <div className='col-lg-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('الفرع المحول منه')}</label>
                  <Select
                    isDisabled={!showModal}
                    classNamePrefix='react-select'
                    isLoading={loading3}
                    isSearchable
                    isClearable
                    options={OperatingUnitOptions}
                    value={OperatingUnitOptions?.find((f: any) => f.value === values.branchId)}
                    onChange={option => {
                      formik.setFieldValue('inventoryId', '');
                      formik.setFieldValue('subInventoryId', null);
                      formik.setFieldValue('branchId', option?.value || '');
                    }}
                    placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                  />
                  <div className='text-danger'>{formik.errors.branchId as any}</div>
                </div>
              </div>

              <div className='col-lg-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('المخزن المحول منه')}</label>
                  <Select
                    isDisabled={!showModal}
                    classNamePrefix='react-select'
                    isLoading={loading3}
                    isSearchable
                    isClearable
                    options={InventoryOptions?.filter((f: any) => f.operatingUnitId === values.branchId)}
                    value={values.inventoryId && InventoryOptions?.find((f: any) => f.value === values.inventoryId)}
                    onChange={(option: any) => {
                      formik.setFieldValue('inventoryId', option === null ? null : option.value);
                      formik.setFieldValue('subInventoryId', null);
                    }}
                    placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                  />
                  <div className='text-danger'>{formik.errors.inventoryId as any}</div>
                </div>
              </div>

              <div className='col-lg-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('الفرع المحول اليه')}</label>
                  <Select
                    classNamePrefix='react-select'
                    isLoading={loading3}
                    isSearchable
                    isClearable
                    options={OperatingUnitOptions2}
                    value={OperatingUnitOptions2?.find((f: any) => f.value === values.branchTo)}
                    onChange={option => {
                      formik.setFieldValue('inventoryTo', '');
                      formik.setFieldValue('subInventoryTo', null);
                      formik.setFieldValue('branchTo', option?.value || '');
                    }}
                    placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  <div className='text-danger'>{formik.errors.branchTo as any}</div>
                </div>
              </div>

              <div className='col-lg-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('المخزن المحول اليه')}</label>
                  <Select
                    classNamePrefix='react-select'
                    isLoading={loading3}
                    isSearchable
                    isClearable
                    options={InventoryOptions2?.filter((f: any) => f.operatingUnitId === values.branchTo)?.filter(
                      (f: any) => f.value !== values.inventoryId
                    )}
                    value={values.inventoryTo && InventoryOptions2?.find((f: any) => f.value === values.inventoryTo)}
                    onChange={(option: any) => {
                      formik.setFieldValue('inventoryTo', option === null ? null : option.value);
                      formik.setFieldValue('subInventoryTo', null);
                    }}
                    placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  <div className='text-danger'>{formik.errors.inventoryTo as any}</div>
                </div>
              </div>

              {/* ✅ طريقة التسعير (methodTransfer) */}
              {/* <div className='col-xl-3'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('طريقة التسعير')}</label>
                  <Form.Select
                    className='form-select'
                    name='methodTransfer'
                    value={values.methodTransfer ?? 0}
                    onChange={e => formik.setFieldValue('methodTransfer', Number(e.target.value || 0))}
                    disabled={!showModal}
                  >
                    <option value={0}>{i18n.language === 'ar' ? 'اختر' : 'Choose'}</option>
                    <option value={1}>
                      {i18n.language === 'ar' ? 'بدون سعر (لا يظهر السعر)' : 'No Price (Hidden)'}
                    </option>
                    <option value={2}>
                      {i18n.language === 'ar' ? 'سعر يدوي (يظهر السعر في البنود)' : 'Manual Price (Shown in lines)'}
                    </option>
                  </Form.Select>
                </div>
              </div> */}

              <div className='col-lg-6'>
                <div className='form-group mb-3'>
                  <label className='form-label'>{t('Note')}</label>
                  <Form.Control
                    autoComplete='off'
                    className='form-control'
                    as='textarea'
                    rows={1}
                    name='description'
                    onChange={formik.handleChange}
                    value={values.description}
                    isInvalid={!!(touched.description && errors.description)}
                  />
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className='shadow mt-3'>
          <MoveLines formik={formik} />
        </Card>
        <Card className='shadow mt-3 p-1'>
          <div className='text-end'>
            <button disabled={formik.isSubmitting} type='submit' className='btn btn-lg me-1 btn-theme rounded-1 '>
              {formik.isSubmitting ? (
                <div className='spinner-border spinner-border-sm me-2'></div>
              ) : (
                <i className='fas fa-lg fa-fw me-2 fa-plus'></i>
              )}
              {t('إضـافة')}
            </button>

            <button type='button' className='btn btn-lg me-1 btn-default rounded-1 ' onClick={() => navigate(-1)}>
              <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
            </button>
          </div>
        </Card>
      </Form>

      {/* ✅ Modal مثل نموذج الفاتورة */}
      <NeatixModal
        show={showModal}
        setShow={setShowModal}
        size='md'
        bodyClassName='p-0'
        centered
        showHeader={false}
        keyboard={false}
        backdrop={true}
      >
        <div className='modal-selection-theme'>
          {/* Header مشابه للمودال المطلوب */}
          <div className='modal-header bg-theme bg-opacity-20 text-dark py-3'>
            <h5 className='modal-title text-center w-100 h5 mb-0'>
              <i className='fas fa-exchange-alt me-2 text-theme'></i>
              {t('اختيار بيانات التحويل')}
            </h5>
          </div>

          <div className='modal-body p-4'>
            <div className='text-center mb-4'>
              <p className='text-muted mb-0'>
                {t('يجب اختيار الوحدة التشغيلية والمخزن ونوع الحركة وطريقة التسعير قبل المتابعة')}
              </p>
            </div>

            <div className='row'>
              {/* الوحدة التشغيلية */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('الوحدة التشغيليـة')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-building text-theme'></i>
                  </span>
                  <Select
                    classNamePrefix='react-select'
                    isSearchable
                    isClearable
                    options={OperatingUnitOptions}
                    value={OperatingUnitOptions?.find((f: any) => f.value === values.branchId)}
                    onChange={option => {
                      formik.setFieldValue('inventoryId', '');
                      formik.setFieldValue('subInventoryId', null);
                      formik.setFieldValue('branchId', option?.value || '');
                    }}
                    placeholder={t('اختر الوحدة التشغيلية')}
                    className='flex-grow-1'
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
              </div>

              {/* المخزن */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('المخزن المحول منه')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-warehouse text-theme'></i>
                  </span>
                  <Select
                    classNamePrefix='react-select'
                    isLoading={loading3}
                    isSearchable
                    isClearable
                    options={InventoryOptions?.filter((f: any) => f.operatingUnitId === values.branchId)}
                    value={values.inventoryId && InventoryOptions?.find((f: any) => f.value === values.inventoryId)}
                    onChange={option => {
                      formik.setFieldValue('inventoryId', option?.value || null);
                      formik.setFieldValue('subInventoryId', null);
                    }}
                    placeholder={t('اختر المخزن')}
                    className='flex-grow-1'
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
              </div>

              {/* نوع الحركة */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('نوع الحركة')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-cogs text-theme'></i>
                  </span>
                  <Select
                    classNamePrefix='react-select'
                    isSearchable
                    isClearable
                    options={OptionsTransactionTypes}
                    value={OptionsTransactionTypes?.find(f => f.value === values.code)}
                    onChange={option => formik.setFieldValue('code', option?.value || null)}
                    placeholder={t('اختر نوع الحركة')}
                    className='flex-grow-1'
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
              </div>

              {/* طريقة التسعير */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('طريقة التسعير')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-dollar-sign text-theme'></i>
                  </span>
                  <div className='d-flex gap-2 p-2 border rounded flex-grow-1' style={{ minHeight: '44px' }}>
                    {/* زر بدون سعر */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='methodTransferModal'
                      id='methodTransferNone'
                      value='1'
                      checked={formik.values.methodTransfer === 1}
                      onChange={() => formik.setFieldValue('methodTransfer', 1)}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        formik.values.methodTransfer === 1 ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='methodTransferNone'
                      style={{ borderRadius: '1px', transition: 'all 0.3s ease' }}
                    >
                      {t('سعر تلقائي من التكلفة')}
                    </label>

                    {/* زر سعر يدوي */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='methodTransferModal'
                      id='methodTransferManual'
                      value='2'
                      checked={formik.values.methodTransfer === 2}
                      onChange={() => formik.setFieldValue('methodTransfer', 2)}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        formik.values.methodTransfer === 2 ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='methodTransferManual'
                      style={{ borderRadius: '1px', transition: 'all 0.3s ease' }}
                    >
                      {t('سعر يدوي')}
                    </label>
                  </div>
                </div>

                {/* ✅ Hint تحت اختيار طريقة التسعير */}
                {formik.values.methodTransfer > 0 && (
                  <div className='mt-2 small text-muted'>
                    <i className='fas fa-info-circle me-2 text-theme'></i>
                    {formik.values.methodTransfer === 1
                      ? t('سيتم احتساب السعر تلقائيًا من تكلفة الصنف ')
                      : t('يتطلب إدخال السعر يدويًا في البنود ')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='modal-footer justify-content-center border-top-0 pt-0'>
            <button
              className='btn btn-outline-secondary btn-lg px-4 py-2 me-3 rounded-2'
              onClick={() => navigate('/INV/Transactions/InvMove')}
            >
              <i className='fas fa-times me-2'></i>
              {t('Cancel')}
            </button>
            <button
              className='btn btn-theme btn-lg px-5 py-2 rounded-2'
              onClick={handleModalSubmit}
              disabled={!values.branchId || !values.inventoryId || !values.code || !values.methodTransfer}
            >
              <i className='fas fa-check-circle me-2'></i>
              {t('Confirm')}
            </button>
          </div>
        </div>
      </NeatixModal>
    </>
  );
};

export default Add;
